import React, { useState } from 'react';
import Logo from '../Layout/Logo';
import { BookOpen, X } from 'lucide-react';

const HeroSection = ({ setActiveSection }) => {
  const [showModal, setShowModal] = useState(false);

  const handleGetStarted = () => {
    setShowModal(true);
  };

  const handleServiceSelect = (section) => {
    setShowModal(false);
    setActiveSection(section);
  };

  return (
    <>
      <section className="relative min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50 to-purple-50 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <div className="text-center">
            {/* Logo - kept unchanged as requested */}
            <div className="flex justify-center mb-8">
              <Logo className="w-32 h-32" />
            </div>
            
            {/* Enhanced Heading with Gradient Text */}
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-gray-900 via-emerald-600 to-blue-600 bg-clip-text text-transparent">
                Smart decisions start with{' '}
              </span>
              <span className="bg-gradient-to-r from-emerald-500 to-blue-500 bg-clip-text text-transparent animate-pulse">
                A.G.O
              </span>
            </h1>
            
            {/* Enhanced Subtitle */}
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Solutions designed to help you make{' '}
              <span className="text-emerald-600 font-semibold">smarter decisions</span> and{' '}
              <span className="text-blue-600 font-semibold">grow with confidence</span>.
            </p>
            
            {/* Enhanced CTA Button - Centered */}
            <div className="flex justify-center">
              <button 
                onClick={handleGetStarted}
                className="group relative px-8 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <span className="relative flex items-center">
                  Get Started
                  <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Modal for Service Selection */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="relative bg-white rounded-3xl shadow-2xl max-w-4xl w-full p-8 animate-fade-in">
            {/* Close Button */}
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>

            {/* Modal Header */}
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                <span className="bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent">
                  Choose Your Path
                </span>
              </h2>
              <p className="text-gray-600 text-lg">
                Select a service to get started with A.G.O
              </p>
            </div>

            {/* Service Options */}
            <div className="flex justify-center">
              {/* A.G.O Learn & Earn Card */}
              <button
                onClick={() => handleServiceSelect('learn')}
                className="group relative bg-gradient-to-br from-purple-50 to-indigo-100 p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer border border-purple-200 hover:border-purple-300 transform hover:scale-105 active:scale-95 text-left max-w-sm w-full"
              >
                <div className="relative">
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-indigo-400 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
                    <div className="relative w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <BookOpen className="text-white w-8 h-8" />
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-indigo-700 transition-colors">
                    A.G.O Learn & Earn
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Transform your skills into income opportunities through our comprehensive learning platform
                  </p>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-indigo-700">
                      <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mr-3"></div>
                      <span>Customer Work Portal</span>
                    </div>
                    <div className="flex items-center text-sm text-indigo-700">
                      <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mr-3"></div>
                      <span>A.G.O Workshop</span>
                    </div>
                    <div className="flex items-center text-sm text-indigo-700">
                      <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mr-3"></div>
                      <span>Skill Development</span>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HeroSection;